import { useMediaQuery, useTheme } from '@mui/material';

import { LAYOUT_CONSTANTS } from '../constants/layout-constants';

export type LayoutCalculations = {
  // Responsive breakpoints
  isDesktop: boolean;
  isTablet: boolean;
  isMobile: boolean;

  // AppBar calculations
  getAppBarWidth: (isSidebarOpen: boolean, isNotificationPanelOpen: boolean) => string;
  getAppBarMarginLeft: (isSidebarOpen: boolean) => number | string;
  getAppBarMarginRight: (isNotificationPanelOpen: boolean) => number | string;

  // Main content calculations
  getMainContentWidth: (isSidebarOpen: boolean, isNotificationPanelOpen: boolean) => string;
  getMainContentMarginLeft: (isSidebarOpen: boolean) => number | string;
  getMainContentMarginRight: (isNotificationPanelOpen: boolean) => number | string;

  // Drawer calculations
  getSidebarDrawerWidth: () => number;
  getNotificationDrawerWidth: () => number | string;
};

/**
 * Custom hook for desktop layout calculations
 * Centralizes all layout width and margin calculations
 */
export const useLayoutCalculations = (): LayoutCalculations => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getAppBarWidth = (isSidebarOpen: boolean, isNotificationPanelOpen: boolean): string => {
    if (!isDesktop) return '100%';

    if (isSidebarOpen && isNotificationPanelOpen) {
      return `calc(100% - ${LAYOUT_CONSTANTS.DRAWER_WIDTH}px - ${LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH}px)`;
    }

    if (isSidebarOpen) {
      return `calc(100% - ${LAYOUT_CONSTANTS.DRAWER_WIDTH}px)`;
    }

    if (isNotificationPanelOpen) {
      return `calc(100% - ${LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH}px)`;
    }

    return '100%';
  };

  const getAppBarMarginLeft = (isSidebarOpen: boolean): number | string => {
    return isDesktop && isSidebarOpen ? `${LAYOUT_CONSTANTS.DRAWER_WIDTH}px` : 0;
  };

  const getAppBarMarginRight = (isNotificationPanelOpen: boolean): number | string => {
    return isDesktop && isNotificationPanelOpen ? `${LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH}px` : 0;
  };

  const getMainContentWidth = (isSidebarOpen: boolean, isNotificationPanelOpen: boolean): string => {
    if (!isDesktop) return '100%';

    if (isSidebarOpen && isNotificationPanelOpen) {
      return `calc(100% - ${LAYOUT_CONSTANTS.DRAWER_WIDTH}px - ${LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH}px)`;
    }

    if (isSidebarOpen) {
      return `calc(100% - ${LAYOUT_CONSTANTS.DRAWER_WIDTH}px)`;
    }

    if (isNotificationPanelOpen) {
      return `calc(100% - ${LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH}px)`;
    }

    return '100%';
  };

  const getMainContentMarginLeft = (isSidebarOpen: boolean): number | string => {
    return isDesktop && isSidebarOpen ? 0 : `-${LAYOUT_CONSTANTS.DRAWER_WIDTH}px`;
  };

  const getMainContentMarginRight = (isNotificationPanelOpen: boolean): number | string => {
    return isDesktop && isNotificationPanelOpen ? 0 : `-${LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH}px`;
  };

  const getSidebarDrawerWidth = (): number => {
    return isDesktop ? LAYOUT_CONSTANTS.DRAWER_WIDTH : LAYOUT_CONSTANTS.DRAWER_WIDTH_MOBILE;
  };

  const getNotificationDrawerWidth = (): number | string => {
    return isDesktop ? LAYOUT_CONSTANTS.NOTIFICATION_PANEL_WIDTH : '90vw';
  };

  return {
    // Responsive breakpoints
    isDesktop,
    isTablet,
    isMobile,

    // Calculation functions
    getAppBarWidth,
    getAppBarMarginLeft,
    getAppBarMarginRight,
    getMainContentWidth,
    getMainContentMarginLeft,
    getMainContentMarginRight,
    getSidebarDrawerWidth,
    getNotificationDrawerWidth
  };
};
